import { TrendingUp, TrendingDown } from "lucide-react";
import {
  LineChart,
  Line,
  ResponsiveContainer,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrency } from "@/utils/format";

const percentFormatter = new Intl.NumberFormat("de-DE", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const formatPercent = (value) => percentFormatter.format((value || 0) / 100);

export default function KPICard({
  title,
  value,
  changePercent,
  metaLabel,
  metaValue,
  sparklineData = [],
  isLoading = false,
  testId,
  valueFormatter,
  changeLabel = "ggü. Vorperiode",
  comparisonValue,
  comparisonLabel,
  comparisonRangeLabel,
  comparisonValueFormatter,
  invertTrend = false,
}) {
  if (isLoading) {
    return (
      <Card data-testid={`${testId}-loading`}>
        <CardContent className="p-6 space-y-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-3 w-16" />
        </CardContent>
      </Card>
    );
  }

  const hasChange = typeof changePercent === "number";
  const trend = hasChange ? Math.sign(changePercent) : 0;
  const trendColor = invertTrend
    ? trend > 0
      ? "text-red-600"
      : trend < 0
        ? "text-green-600"
        : "text-gray-600"
    : trend > 0
      ? "text-green-600"
      : trend < 0
        ? "text-red-600"
        : "text-gray-600";
  const trendIcon = trend > 0 ? TrendingUp : trend < 0 ? TrendingDown : null;
  const TrendIcon = trendIcon;

  const formatValue = valueFormatter || formatCurrency;
  const formatComparison = comparisonValueFormatter || valueFormatter || formatCurrency;
  const hasComparison = typeof comparisonValue === "number";
  const comparisonText = hasComparison
    ? `${comparisonLabel || "Vorperiode"}${comparisonRangeLabel ? ` (${comparisonRangeLabel})` : ""}: ${formatComparison(comparisonValue)}`
    : null;

  return (
    <Card data-testid={testId}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm text-gray-600">{title}</CardTitle>
        {TrendIcon && <TrendIcon className={`h-4 w-4 ${trendColor}`} data-testid={`${testId}-trend-icon`} />}
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="text-2xl font-semibold text-gray-900" data-testid={`${testId}-value`}>
          {formatValue(value)}
        </div>
        <div className={`text-xs ${trendColor}`} data-testid={`${testId}-change`}>
          {hasChange ? `${formatPercent(changePercent)} ${changeLabel}` : "—"}
        </div>
        {comparisonText && (
          <div className="text-[11px] text-gray-500" data-testid={`${testId}-comparison`}>
            {comparisonText}
          </div>
        )}
        {metaLabel && (
          <div className="text-xs text-gray-500" data-testid={`${testId}-meta`}>
            {metaLabel}: {metaValue}
          </div>
        )}
        {sparklineData.length > 1 && (
          <div className="h-10" data-testid={`${testId}-sparkline`}>
            <ResponsiveContainer width="100%" height="100%" minHeight={40} minWidth={80}>
              <LineChart data={sparklineData}>
                <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}